import * as Yup from "yup";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// routes
import { PATH_AUTH } from "../../../routes/paths";
// hooks
import useAuth from "../../../hooks/useAuth";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
// components
import Iconify from "../../../components/Iconify";
import {
  FormProvider,
  RHFTextField,
  RHFCheckbox,
  RHFPhoneField,
} from "../../../components/hook-form";

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    phone: Yup.string().required("this field is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    phone: "",
    password: "",
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const navigate = useNavigate();

  function extractErrorMessage(error) {
    // Check if `response` and `data` exist
    if (error.response?.data) {
      const { data } = error.response;

      if (typeof data === "string") {
        return data;
      }

      if (Array.isArray(data)) {
        return data[0];
      }

      if (data.detail) {
        return data.detail;
      }

      if (data.phone?.length) {
        return data.phone[0];
      }
    }

    // Check if `response` and `error` exist
    if (error.response?.error) {
      const { error: errData } = error.response;

      if (typeof errData === "string") {
        return errData;
      }

      if (Array.isArray(errData)) {
        return errData[0];
      }

      if (errData.detail) {
        return errData.detail;
      }
    }

    // Default fallback error message
    return "An error occurred. Please try again.";
  }

  const onSubmit = async (data) => {
    try {
      await login(data.phone, data.password);
      return navigate("/");
    } catch (error) {
      console.error(error);
      reset();
      if (isMountedRef.current) {
        setError("afterSubmit", {
          ...error,
          message: extractErrorMessage(error) || "errorOccurred",
        });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        <RHFPhoneField type="tel" name="phone" label="Phone" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ my: 2 }}
      >
        <RHFCheckbox name="remember" label="Souviens-toi de moi" />
        <Link
          component={RouterLink}
          variant="subtitle2"
          to={PATH_AUTH.resetPassword}
        >
          Mot de passe oublié?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Se connecter
      </LoadingButton>
    </FormProvider>
  );
}
